import { createMarketTrends } from 'app/shared/models/MarketTrends';
import {
  getMedianRentByBedroom as fetchMedianRentByBedroomData,
  getMarketTrendsSummary,
  getTopRentalMarkets,
  growthClientWithRequestContext,
} from '@zg-rentals/rental-growth-api';
import { DatarouterError } from '@zg-rentals/web-base';
import constants from 'app/shared/flux/constants';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { isEmpty } from 'app/shared/utils/marketTrendsUtils';
import MedianRentByBedroom from 'app/shared/models/MedianRentByBedroom';

const NON_CRITICAL_ERRORS = ['INVALID_INPUT'];

export const getMarketTrends = (params: {
  resourceId: string;
  bedrooms?: string;
  propertyTypes?: string;
  otherResourceId?: string;
}) => {
  const { resourceId, bedrooms, propertyTypes, otherResourceId } = params;
  // @ts-ignore
  return async (dispatch, getState) => {
    let res = {};
    let response;
    try {
      response = await getMarketTrendsSummary(
        {
          resourceId,
          beds: bedrooms,
          propertyTypes,
          rentCompareResourceId: otherResourceId,
        },
        growthClientWithRequestContext(getState()),
      );
    } catch (error) {
      // Non-critical errors (e.g. INVALID_INPUT) leave `response` undefined, which yields empty
      // market trends and triggers the top-markets / 404 fallback below. Re-throw everything else
      // so the SSR/Filter caller can log it and return a 404.
      if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
        throw error;
      }
    }

    const marketTrends = createMarketTrends(response, resourceId, otherResourceId);
    const hasFilters = bedrooms || propertyTypes;
    if (!hasFilters) {
      dispatch({ type: constants.SET_UNFILTERED_MARKET_STATS, payload: marketTrends });
    }
    if (isEmpty(marketTrends)) {
      await dispatch(fetchTopMarkets(params));
      res = { changeHttpStatus: 404 };
    }

    dispatch({ type: constants.SET_MARKET_STATS, payload: marketTrends });
    return res;
  };
};

export const getUnfilteredMarketTrends = (resourceId: string) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    let response;
    try {
      response = await getMarketTrendsSummary({ resourceId }, growthClientWithRequestContext(getState()));
    } catch (error) {
      if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
        throw error;
      }
    }

    dispatch({
      type: constants.SET_UNFILTERED_MARKET_STATS,
      payload: createMarketTrends(response, resourceId),
    });
    return {};
  };
};

export const fetchTopMarkets = (params: { bedrooms?: string; propertyTypes?: string }) => {
  const { bedrooms, propertyTypes } = params;
  // @ts-ignore
  return async (dispatch, getState) => {
    const { topRentalMarkets } = await getTopRentalMarkets(
      { beds: bedrooms, propertyTypes },
      growthClientWithRequestContext(getState()),
    );
    dispatch({
      type: constants.SET_TOP_MARKETS,
      payload: {
        topRentalMarkets: topRentalMarkets.map((market) => ({
          url: `${pathsManifest.MarketTrendsPage}/${market.resourceId}/`,
          resourceId: market.resourceId,
          name: market.areaName,
          medianRent: market.medianRent,
          prices: market.medianRentPriceOverTime,
        })),
      },
    });
    return {};
  };
};

export const getMedianRentByBedroom = (resourceId: string) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    let response;
    try {
      response = await fetchMedianRentByBedroomData({ resourceId }, growthClientWithRequestContext(getState()));
    } catch (error) {
      if (!(DatarouterError.is(error) && NON_CRITICAL_ERRORS.includes(error.code))) {
        throw error;
      }
    }

    dispatch({
      type: constants.SET_MEDIAN_RENT_BY_BEDROOM,
      payload: new MedianRentByBedroom({
        studioMedianRent: response?.studioMedianRent ?? null,
        oneBedroomMedianRent: response?.oneBedroomMedianRent ?? null,
        twoBedroomMedianRent: response?.twoBedroomMedianRent ?? null,
        threeBedroomMedianRent: response?.threeBedroomMedianRent ?? null,
        fourPlusBedroomMedianRent: response?.fourPlusBedroomMedianRent ?? null,
      }),
    });
    return {};
  };
};
